import { ProgressEvent } from '../../adapters';
import {
    InstanceManager,
    MediaControllerState,
    MediaControllerStateSnapshot,
    PlanetMediaPlayer,
    PlanetUserInteractionArgs,
    PlaybackSessionState,
} from '../../media-player';
import { MediaItem, PlanetMediaPlayerEvents } from '../../models';
import { AbstractPlugin } from '../abstract-plugin';
import { OnError, OnUserInteraction, VideoAnalyticsPluginOptions } from '../models';
import {
    BrowserConsoleTracker,
    PlanetAnalyticsTracker,
    PlaybackSessionTracker,
    ShinyStatTracker,
    TrackerOptions,
    VideoAnalyticsTrackerConfiguration,
    VideoAnalyticsTrackerType,
} from './trackers';
import { GoogleAnalyticsTracker } from './trackers/google-analytics-tracker';
import { MatomoAnalyticsTracker } from './trackers/matomo-analytics-tracker';

export class VideoAnalyticsPlugin extends AbstractPlugin<VideoAnalyticsPluginOptions>
    implements OnUserInteraction, OnError {

    static readonly PluginName: string = 'VideoAnalytics';

    protected trackers: PlaybackSessionTracker<VideoAnalyticsTrackerType, TrackerOptions>[] = [];

    constructor() {
        super();
    }

    async setup(player: PlanetMediaPlayer, options?: TrackerOptions): Promise<void> {
        await super.setup(player, options);
        this.player.on(PlanetMediaPlayerEvents.UserInteraction, this.onUserInteraction.bind(this));
        this.trackers = (await this.setupTrackers()).filter(t => t !== null);
    }

    async setupTrackers(): Promise<PlaybackSessionTracker<VideoAnalyticsTrackerType, TrackerOptions>[]> {
        return Promise.all(this.options.trackers.map(async (config: VideoAnalyticsTrackerConfiguration) => {
            const tracker: PlaybackSessionTracker<VideoAnalyticsTrackerType, TrackerOptions> = this.createTracker(config.type);
            if (tracker) {
                try {
                    return await tracker.setup(this.player, config.options);
                } catch (e) {
                    return null;
                }
            }
        }));
    }

    createTracker(type: VideoAnalyticsTrackerType): PlaybackSessionTracker<VideoAnalyticsTrackerType, TrackerOptions> {
        switch (type) {
            case VideoAnalyticsTrackerType.ShinyStat:
                return new ShinyStatTracker();
            case VideoAnalyticsTrackerType.BrowserConsole:
                return new BrowserConsoleTracker();
            case VideoAnalyticsTrackerType.Garavot:
                return new PlanetAnalyticsTracker();
            case VideoAnalyticsTrackerType.GoogleAnalytics:
                return new GoogleAnalyticsTracker();
            case VideoAnalyticsTrackerType.Matomo:
                return new MatomoAnalyticsTracker();
            default:
                return null;
        }
    }

    onPlaylist(playlist: MediaItem[]): void {
        //no-op
    }

    onPlaylistItem(item: MediaItem, index: number): void {
        //this.trackers.forEach(t => t.onContentLoad(item, index));
    }

    onSeek(from, to): void {
        //super.onSeek(from, to);
    }

    onSeeked(position: number): void {
        //this.trackers.map(t => t.onSeeked(position));
    }

    onDispose(): void {
        this.dispose();
    }

    onReady(): void {
        // da valutare cosa vuol dire "ready", il comportamento e' diverso per i diversi player
        // per ora considero solo il momento in cui e' possibile riprodurre il video.
    }

    onStateChange(state: MediaControllerState, previousState: MediaControllerState, stateSnapshot: MediaControllerStateSnapshot): void {
        this.trackers.forEach(tracker => tracker.trackStateChange(previousState, state));
    }

    onComplete(): void {
        //this.trackers.forEach(t => t.onComplete());
    }

    onUserInteraction(data: PlanetUserInteractionArgs): void {
        //this.trackers.forEach(t => t.onUserInteraction(data));
    }

    dispose(): void {
        this.trackers.forEach(t => t.dispose());
    }

    onError(error: Error): void {
        //this.trackers.forEach(t => t.onError(error))
    }

    onSetupError(setupError: Error): void {
        //this.trackers.forEach(t => t.onSetupError(setupError))
    }

    onPlaybackSessionCreate(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackSessionCreate(state));
    }

    onPlaybackSessionEnd(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackSessionEnd(state));
    }

    onPlaybackSessionPause(state: PlaybackSessionState): void {
        this.trackers.forEach(tracker => {
            tracker.trackPlaybackPause(
                state.currentSegment.end,
                state.content.duration,
                this.player.getState(),
                state.currentSegment,
            );
        });
    }

    onPlaybackSessionResume(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackPlaybackResume(state.currentSegment.start, state.content.duration));
    }

    onPlaybackSessionSeek(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackSeek(state.currentPosition));
    }

    onPlaybackSessionStart(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackPlaybackStart(state.currentSegment.start, state.content.duration));
    }

    onPlaybackSessionContentLoad(state: PlaybackSessionState): void {
        this.trackers.forEach(t => t.trackContentLoad(state.content, state.content.index));
    }

    onBufferChange(data: ProgressEvent): void {
        this.trackers.forEach(t => t.onBufferChange(data));
    }
}

InstanceManager.registerPlugin(VideoAnalyticsPlugin.PluginName, VideoAnalyticsPlugin);
